import './styles/CalendarEntryDetail.scss';

import { useQuery } from '@apollo/client';
import { GetApp } from '@mui/icons-material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Close from '@mui/icons-material/Close';
import {
  Grid,
  Paper,
  Divider,
  Button,
  Icon,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';

import parse from 'html-react-parser';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import QUERY from 'gql/queries/getArticle.gql';

import { DenseCalendar } from 'app/components/DenseCalendar';
import DetailPage from 'app/components/DetailPage';
import ArticleTypeLogo from 'app/components/RichList/items/ArticleItem/components/ArticleTypeLogo';
import Loader from 'app/components/Util/Loader';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import '../styles/Calendar.scss';

interface Props {
  id: string;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CalendarEntryDetail: FunctionComponent<Props> = ({ id, setDialogOpen }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { loading, error, data } = useQuery<any, any>(QUERY, {
    variables: { id: parseInt(id, 10) }
  });
  if (loading) {
    return <Loader backdrop={false} />;
  }

  if (error) {
    if (error.graphQLErrors[0]?.extensions?.status === 404) {
      dispatch(AlertActions.error(`${t(translations.pages.calendar.detail.notFound())}`));
    } else {
      dispatch(
        AlertActions.error(
          `${t(translations.pages.calendar.detail.openFailed())}: ${error.message}`
        )
      );
    }
    setDialogOpen(false);
  }

  if (data?.article === undefined) {
    return null;
  }
  const { article } = data;

  const sideInfo = () => {
    const { appointment } = article;
    if (!appointment) {
      return null;
    }

    return (
      <>
        <IconButton
          className="calendar-article-detail__back-button"
          onClick={() => setDialogOpen(false)}
        >
          {smallAndDown ? <ArrowBackIosIcon fontSize="large" /> : <Close fontSize="large" />}
        </IconButton>
        <Grid
          item
          className="calendar-article-detail__appointment"
          justifyContent="center"
          alignContent="center"
          container={smallAndDown}
        >
          <Grid container className="calendar-article-detail__appointment__heading">
            {t(translations.common.event())}
          </Grid>
          <Grid
            className="calendar-article-detail__calendar"
            justifyContent="center"
            alignContent="center"
            xs={12}
          >
            <Grid container component={Paper} elevation={4}>
              <DenseCalendar
                startDate={appointment.startDate}
                endDate={appointment.endDate}
                markDates
              />
            </Grid>
          </Grid>
          <Grid container className="calendar-article-detail__content__aside__icon">
            <Divider variant="middle" className="divider" style={{ width: '100%' }} />
          </Grid>
          <Grid container className="calendar-article-detail__content__aside__icon">
            <ArticleTypeLogo articleType={article.type} />
          </Grid>
          <Grid spacing={2} container className="calendar-article-detail__content__aside__type">
            {t(translations.pages.articles.type[article.type]())}
          </Grid>
          <Grid container className="calendar-article-detail__appointment__date">
            <Grid container className="calendar-article-detail__appointment__date__heading">
              <Icon className="far fa-clock" style={{ width: 'auto' }} />
              {t(translations.common.start())}
            </Grid>
            {new Date(appointment.startDate).toLocaleString()}
          </Grid>
          <Grid container className="calendar-article-detail__appointment__date">
            <Grid container className="calendar-article-detail__appointment__date__heading">
              <Icon className="fas fa-clock" style={{ width: 'auto' }} />
              {t(translations.common.end())}
            </Grid>{' '}
            {new Date(appointment.endDate).toLocaleString()}
          </Grid>
          {appointment.venue && (
            <Grid container className="calendar-article-detail__appointment__date">
              <Grid container className="calendar-article-detail__appointment__date__heading">
                <Icon className="fas fa-map-pin" style={{ width: 'auto' }} />
                {t(translations.pages.articles.create.lifetime.venue())}
              </Grid>
              {appointment.venue}
            </Grid>
          )}
          <Grid item xs={12} md className="calendar-article-detail__content__aside">
            {article.attachement && (
              <Grid container justifyContent="center">
                <Button
                  size="small"
                  className="calendar-article-detail__content__aside__download"
                  startIcon={<GetApp />}
                  onClick={() =>
                    handleDownload(
                      article.attachement.split('__', 2)[1],
                      `${import.meta.env.VITE_GATEWAY_URL}/articles/v1/articles/${
                        article.id
                      }/attachement`
                    )
                  }
                >
                  {t(translations.pages.jobOffers.downloadAttachement())}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <DetailPage scrollable={false} sideInfo={sideInfo()} dialog className="calendar-article-detail">
      <Grid container className="calendar-article-detail__content">
        <Grid item xs={12} md={10} className="calendar-article-detail__content__main">
          <Grid container className="calendar-article-detail__content__main__headline">
            {article.headline}
          </Grid>
          <Grid container className="calendar-article-detail__content__main__author">
            {t(translations.pages.articleDetail.articleBy(), {
              author: `${article.author.firstName} ${article.author.lastName}`,
              date: new Date(article.startDate).toLocaleDateString()
            })}
          </Grid>
          <Grid container className="calendar-article-detail__content__main__short-description">
            {article.shortDescription}
          </Grid>
          <Grid container className="calendar-article-detail__content__main__article-text">
            {parse(article.articleText)}
          </Grid>
        </Grid>
      </Grid>
    </DetailPage>
  );
};
